import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Filter, MapPin, Clock } from 'lucide-react';

const SearchBar = ({
  value = '',
  onChange,
  onSearch,
  onClear,
  placeholder = 'Search...',
  suggestions = [],
  showSuggestions = false,
  loading = false,
  size = 'md',
  variant = 'default',
  showFilters = false,
  filters = [],
  onFilterChange,
  showLocation = false,
  onLocationClick,
  recentSearches = [],
  onRecentSearchClick,
  debounceMs = 300,
  className = ""
}) => {
  const [focused, setFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Size variants
  const sizeClasses = {
    sm: {
      input: 'px-3 py-2 text-sm',
      icon: 'w-4 h-4',
      button: 'px-2 py-1'
    },
    md: {
      input: 'px-4 py-3 text-base',
      icon: 'w-5 h-5',
      button: 'px-3 py-2'
    },
    lg: {
      input: 'px-5 py-4 text-lg',
      icon: 'w-6 h-6',
      button: 'px-4 py-3'
    }
  };

  // Variant styles
  const variants = {
    default: 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
    filled: 'bg-gray-100 border-transparent focus:bg-white focus:border-primary-500 focus:ring-primary-500',
    minimal: 'border-0 border-b-2 border-gray-200 rounded-none focus:border-primary-500 focus:ring-0 bg-transparent'
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variants[variant];

  // Debounced search
  useEffect(() => {
    if (debounceMs && onChange) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onChange(value);
      }, debounceMs);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, onChange, debounceMs]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange?.(newValue);
    
    if ((suggestions.length > 0 || recentSearches.length > 0) && showSuggestions) {
      setShowDropdown(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onSearch?.(value.trim());
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion) => {
    onChange?.(suggestion);
    onSearch?.(suggestion);
    setShowDropdown(false);
  };

  const handleRecentSearchClick = (search) => {
    onChange?.(search);
    onRecentSearchClick?.(search);
    setShowDropdown(false);
  };

  const handleFocus = () => {
    setFocused(true);
    if ((suggestions.length > 0 || recentSearches.length > 0) && showSuggestions) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // Delay hiding dropdown to allow clicks on suggestions
    setTimeout(() => setShowDropdown(false), 200);
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className={`flex items-center rounded-md border shadow-sm transition-all duration-200 ${currentVariant} ${
          focused ? 'ring-1' : ''
        }`}>
          {/* Location button */}
          {showLocation && (
            <button
              type="button"
              onClick={onLocationClick}
              className={`flex items-center space-x-2 text-gray-500 hover:text-primary-600 ${currentSize.button} border-r border-gray-200`}
            >
              <MapPin className={currentSize.icon} />
              <span className="hidden sm:inline text-sm">Near me</span>
            </button>
          )}

          {/* Search input */}
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              className={`w-full ${currentSize.input} bg-transparent border-0 focus:ring-0 focus:outline-none placeholder-gray-400`}
            />
          </div>

          {/* Clear button */}
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className={currentSize.icon} />
            </button>
          )}

          {/* Search button */}
          <button
            type="submit"
            disabled={loading || !value.trim()}
            className={`flex items-center justify-center ${currentSize.button} text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-md transition-colors duration-200`}
          >
            {loading ? (
              <svg className={`${currentSize.icon} animate-spin`} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <Search className={currentSize.icon} />
            )}
          </button>

          {/* Filters button */}
          {showFilters && filters.length > 0 && (
            <button
              type="button"
              onClick={() => onFilterChange?.(filters)}
              className={`flex items-center space-x-1 ${currentSize.button} text-gray-500 hover:text-primary-600 border-l border-gray-200`}
            >
              <Filter className={currentSize.icon} />
              <span className="hidden sm:inline text-sm">Filters</span>
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showDropdown && showSuggestions && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {/* Recent Searches */}
          {recentSearches.length > 0 && !value && (
            <div className="p-2">
              <div className="flex items-center space-x-2 px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <Clock className="w-3 h-3" />
                <span>Recent Searches</span>
              </div>
              {recentSearches.slice(0, 5).map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearchClick(search)}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors duration-150"
                >
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{search}</span>
                </button>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="p-2">
              {!value && recentSearches.length > 0 && (
                <hr className="my-2 border-gray-100" />
              )}
              {value && (
                <div className="flex items-center space-x-2 px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <Search className="w-3 h-3" />
                  <span>Suggestions</span>
                </div>
              )}
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors duration-150"
                >
                  <Search className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{suggestion}</span>
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {suggestions.length === 0 && recentSearches.length === 0 && value && (
            <div className="p-4 text-center text-sm text-gray-500">
              No suggestions found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;